// src/components/PDFPreview.jsx
import React from "react";

/**
 * Convierte rutas locales a URLs seguras (codifica espacios, acentos, paréntesis, etc.)
 * - Mantiene URLs absolutas (http/https) tal cual.
 * - Para rutas relativas, devuelve una ruta ABSOLUTA en el mismo origen (empieza por "/").
 * - Codifica cada segmento una sola vez (evita doble-encoding).
 */
export const assetURL = (rawPath = "") => {
  if (!rawPath) return "";
  let p = String(rawPath).normalize("NFC");
  // Si ya es URL absoluta, no tocarla
  if (/^https?:\/\//i.test(p)) return p;
  // Asegura barra inicial
  if (!p.startsWith("/")) p = `/${p}`;
  // Codifica segmento a segmento
  const encoded = p
    .split("/")
    .map((seg, i) => {
      if (i === 0 || seg === "") return seg; // conserva el primer "" de la barra inicial
      try {
        return encodeURIComponent(decodeURIComponent(seg));
      } catch {
        return encodeURIComponent(seg);
      }
    })
    .join("/");
  // Importante: devolvemos ruta relativa al mismo origen (NO añadimos window.location.origin)
  // para evitar problemas de CORS y SSR.
  return encoded;
};

/**
 * PDFPreview
 * - Muestra PDFs embebidos con el visor del navegador.
 * - Por defecto deja la UI del visor activa (interacción “como antes”).
 * - Ajuste al ancho (zoom=page-width) para tener solo scroll vertical.
 * - Evita descargas automáticas.
 */
export default function PDFPreview({ src, title = "PDF", className = "", showToolbar = true }) {
  const base = assetURL(src);

  // Preferimos "page-width" (Chrome/Edge) y añadimos FitH como fallback para otros visores.
  const viewerHash = showToolbar
    ? "#page=1&zoom=page-width&view=FitH"
    : "#page=1&zoom=page-width&view=FitH&toolbar=0&navpanes=0&statusbar=0&messages=0";

  const url = `${base}${viewerHash}`;

  return (
    <div className={`relative w-full h-full overflow-x-hidden ${className}`}>
      {/* <object> es el principal; si el navegador no puede, caerá en <embed> y luego <iframe> */}
      <object data={url} type="application/pdf" className="w-full h-full">
        <embed src={url} type="application/pdf" className="w-full h-full" />
        <iframe title={title || "PDF"} src={url} className="w-full h-full" />
        {/* Fallback final: botón para abrir en nueva pestaña */}
        <div className="absolute inset-0 flex items-center justify-center bg-white">
          <a
            href={base}
            target="_blank"
            rel="noreferrer"
            className="px-3 py-2 rounded-xl border bg-white hover:bg-gray-50 text-sm font-medium"
          >
            {title || "Obrir PDF"}
          </a>
        </div>
      </object>

      {/* Chip “PDF” */}
      <div className="absolute left-2 top-2 text-[10px] uppercase tracking-wide bg-emerald-600 text-white px-2 py-0.5 rounded">
        PDF
      </div>
    </div>
  );
}
